import React from 'react';
import type { WidgetItem, ReportGlobalSettings } from './types';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { FileText, ArrowLeft, Save, RefreshCw } from 'lucide-react';

interface TopToolbarProps {
  selectedWidget?: WidgetItem | null;
  globalSettings?: ReportGlobalSettings;
  onBackToReports?: () => void;
  onSaveReport?: () => void;
  onRefresh?: () => void;
  isSaving?: boolean;
}

const TopToolbar: React.FC<TopToolbarProps> = ({
  globalSettings,
  onBackToReports,
  onSaveReport,
  onRefresh,
  isSaving = false
}) => {
  return (
    <Box
      sx={{
        px: 2,
        height: 42,
        borderBottom: '1px solid #1e293b',
        bgcolor: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        zIndex: 20
      }}
    >
      {/* Left: Back button & Title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {onBackToReports && (
          <Tooltip title="Back to Reports list" arrow placement="bottom">
            <Button
              size="small"
              startIcon={<ArrowLeft size={14} />}
              onClick={onBackToReports}
              sx={{
                height: 28,
                fontSize: 11.5,
                fontWeight: 600,
                textTransform: 'none',
                color: '#cbd5e1',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                px: 1.25,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              Back to Reports
            </Button>
          </Tooltip>
        )}

        <Box sx={{ width: '1px', height: 16, bgcolor: '#334155' }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileText size={15} color="#f97316" />
          <Typography
            sx={{
              color: '#f8fafc',
              fontWeight: 600,
              fontSize: 13,
              maxWidth: 380,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {globalSettings?.reportTitle || 'Report Canvas'}
          </Typography>
        </Box>
      </Box>

      {/* Right: Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {onRefresh && (
          <Tooltip title="Refresh Builder" arrow placement="bottom">
            <Button
              size="small"
              startIcon={<RefreshCw size={13} />}
              onClick={onRefresh}
              sx={{
                height: 28,
                fontSize: 11.5,
                fontWeight: 600,
                textTransform: 'none',
                color: '#cbd5e1',
                bgcolor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                px: 1.25,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              Refresh
            </Button>
          </Tooltip>
        )}

        {onSaveReport && (
          <Button
            size="small"
            variant="contained"
            disabled={isSaving}
            startIcon={<Save size={13} />}
            onClick={onSaveReport}
            sx={{
              height: 28,
              fontSize: 11.5,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#10b981',
              color: '#ffffff',
              borderRadius: '6px',
              px: 1.5,
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: '#059669',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(16, 185, 129, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Report'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default TopToolbar;
